import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setCurrentUser } from '../hooks/useAuth';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authentication...');

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const token = searchParams.get('token');
        const provider = searchParams.get('provider');
        const error = searchParams.get('error');

        if (error) {
          setStatus('error');
          setMessage(`Authentication failed: ${error.replace(/_/g, ' ')}`);
          setTimeout(() => {
            navigate('/signin', { state: { error: `${provider} authentication failed` } });
          }, 3000);
          return;
        }

        if (!token) {
          setStatus('error');
          setMessage('No authentication token received');
          setTimeout(() => {
            navigate('/signin');
          }, 3000);
          return;
        }

        // Decode the user token
        const userDataJson = atob(token); // Use atob instead of Buffer for browser compatibility
        const userData = JSON.parse(userDataJson);

        // Store user info in localStorage
        setCurrentUser(userData);

        setStatus('success');
        setMessage(`Successfully signed in with ${provider}!`);

        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 2000);

      } catch (error) {
        console.error('Auth callback error:', error);
        setStatus('error');
        setMessage('Authentication processing failed');
        setTimeout(() => {
          navigate('/signin');
        }, 3000);
      }
    };

    handleAuthCallback();
  }, [navigate, searchParams]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      textAlign: 'center',
      padding: '2rem'
    }}>
      <div style={{
        background: 'white',
        color: '#333',
        padding: '2rem',
        borderRadius: '1rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        maxWidth: '400px',
        width: '100%'
      }}>
        {status === 'loading' && (
          <>
            <div style={{
              width: '3rem',
              height: '3rem',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <h2 style={{ margin: '0 0 1rem 0' }}>Processing...</h2>
            <p style={{ margin: 0, color: '#666' }}>{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{
              fontSize: '3rem',
              marginBottom: '1rem'
            }}>✅</div>
            <h2 style={{ margin: '0 0 1rem 0', color: '#059669' }}>Success!</h2>
            <p style={{ margin: 0, color: '#666' }}>{message}</p>
            <p style={{ margin: '1rem 0 0 0', color: '#999', fontSize: '0.9rem' }}>
              Redirecting to dashboard...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{
              fontSize: '3rem',
              marginBottom: '1rem'
            }}>❌</div>
            <h2 style={{ margin: '0 0 1rem 0', color: '#dc2626' }}>Error</h2>
            <p style={{ margin: 0, color: '#666' }}>{message}</p>
            <p style={{ margin: '1rem 0 0 0', color: '#999', fontSize: '0.9rem' }}>
              Redirecting to sign in...
            </p>
          </>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AuthCallback;